import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Table,
  Button,
  Input,
  Modal,
  Form,
  message,
} from "antd";
import {
  PlusOutlined,
} from "@ant-design/icons";
import { useSelector } from "react-redux";
import "../../components/Discounts/Discounts.css";
import { translations } from "../../utils/Translation/staticTranslation";
import { AnnouncementsApi } from "../../Apis/announcementsApi";
import DeleteConfirmationModal from "../../components/Common/Modals/DeleteConfirmationModal";
import {
  isProfileModuleDeleteBlocked,
  isProfileModuleReadOnly,
} from "../../utils/modulePermissionSidebar";

const Announcements = () => {
  const user = useSelector((state) => state.user.user);
  const readOnly = isProfileModuleReadOnly(user, "announcements");
  const deleteBlocked = isProfileModuleDeleteBlocked(user, "announcements");
  const lang = localStorage.getItem("language") || "ar";
  const t = translations[lang]?.announcementsPage;
  const isRTL = lang === "ar";

  useEffect(() => {
    if (readOnly) setShowModal(false);
    if (deleteBlocked) {
      setShowDeleteModal(false);
      setAnnouncementToDelete(null);
    }
  }, [readOnly, deleteBlocked]);

  const [form] = Form.useForm();
  const [showModal, setShowModal] = useState(false);
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [announcementToDelete, setAnnouncementToDelete] = useState(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [charCounts, setCharCounts] = useState({
    english: 0,
    arabic: 0,
  });

  const MAX_CHARS = 70;
  const countChars = (text = "") => text.trim().length;

  const normalizeAnnouncement = (item) => ({
    id: item?.id ?? item?.uuid ?? Date.now(),
    englishText: item?.english_text ?? item?.englishText ?? "",
    arabicText: item?.arabic_text ?? item?.arabicText ?? "",
  });

  const fetchAnnouncements = async (page = pagination.current, opts = {}) => {
    const { cacheBust } = opts;
    try {
      setLoading(true);
      const response = await AnnouncementsApi.getAnnouncements(
        page,
        pagination.pageSize,
        cacheBust
      );
      const data =
        response?.data?.results ??
        response?.data?.data ??
        response?.data ??
        [];
      const normalized = Array.isArray(data)
        ? data.map(normalizeAnnouncement)
        : [];
      const totalCount = response?.data?.count ?? normalized.length;
      setAnnouncements(normalized);
      setPagination((prev) => ({
        ...prev,
        current: page,
        total: totalCount,
      }));
    } catch (err) {
      console.error("Error fetching announcements", err);
      message.error(t?.error_fetching || "Error fetching announcements");
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch and on page change
  useEffect(() => {
    fetchAnnouncements(pagination.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.current]);

  const openCreateModal = () => {
    if (readOnly) return;
    form.resetFields();
    setCharCounts({ english: 0, arabic: 0 });
    setShowModal(true);
  };

  const handleSubmit = async () => {
    if (readOnly) return;
    const englishValue = form.getFieldValue("englishText") || "";
    const arabicValue = form.getFieldValue("arabicText") || "";
    const englishChars = countChars(englishValue);
    const arabicChars = countChars(arabicValue);
    if (englishChars > MAX_CHARS || arabicChars > MAX_CHARS) {
      message.error(
        t?.validation?.max_chars || `Maximum ${MAX_CHARS} characters allowed`
      );
      return;
    }
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      const payload = {
        english_text: values.englishText.trim(),
        arabic_text: values.arabicText.trim(),
      };
      const response = await AnnouncementsApi.createAnnouncement(payload);
      const createdData =
        response?.data?.data ?? response?.data?.result ?? response?.data;
      const created = normalizeAnnouncement(createdData || payload);
      // After create, refetch first page to keep ordering consistent
      setPagination((prev) => ({ ...prev, current: 1 }));
      setAnnouncements((prev) => [created, ...prev]);
      setShowModal(false);
      form.resetFields();
      message.success(t?.success_create || "Announcement created successfully");
    } catch (error) {
      // Validation handled by antd form rules
      if (error) {
        console.error("Create announcement error", error);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const confirmDelete = () => {
    if (deleteBlocked) return;
    if (!announcementToDelete) return;
    const id = announcementToDelete.id;
    setDeletingId(id);
    AnnouncementsApi.deleteAnnouncement(id)
      .then(() => {
        // Optimistically update local list
        setAnnouncements((prev) => prev.filter((item) => item.id !== id));
        // Recalculate pagination and refetch to stay in sync
        setPagination((prev) => {
          const newTotal = Math.max((prev.total || 1) - 1, 0);
          const lastPage = Math.max(1, Math.ceil(newTotal / prev.pageSize));
          const targetPage = Math.min(prev.current, lastPage);
          // Ensure we refetch even if page doesn't change (cache-bust)
          setTimeout(() => fetchAnnouncements(targetPage, { cacheBust: Date.now() }), 0);
          return { ...prev, current: targetPage, total: newTotal };
        });
        message.success(t?.delete?.success || "Announcement deleted");
        setShowDeleteModal(false);
        setAnnouncementToDelete(null);
      })
      .catch((err) => {
        console.error("Delete announcement error", err);
        message.error(t?.delete?.error || "Unable to delete announcement");
      })
      .finally(() => setDeletingId(null));
  };

  const handleDeleteClick = useCallback(
    (record) => {
      if (deleteBlocked) return;
      setAnnouncementToDelete(record);
      setShowDeleteModal(true);
    },
    [deleteBlocked],
  );

  const columns = useMemo(() => {
    const base = [
      {
        title: t?.table?.english_text || "English Text",
        dataIndex: "englishText",
        key: "englishText",
        width: 300,
        ellipsis: true,
        render: (text) => (
          <div className="discount-code-cell" style={{ direction: "ltr" }}>
            {text || "—"}
          </div>
        ),
      },
      {
        title: t?.table?.arabic_text || "Arabic Text",
        dataIndex: "arabicText",
        key: "arabicText",
        width: 300,
        ellipsis: true,
        render: (text) => (
          <div
            className="discount-description-cell"
            style={{ direction: "rtl", textAlign: "right" }}
          >
            {text || "—"}
          </div>
        ),
      },
      {
        title: t?.table?.actions || "Actions",
        key: "actions",
        width: 140,
        render: (_, record) =>
          deleteBlocked ? null : (
          <Button
            type="text"
            style={{
              padding: 0,
              display: "flex",
              alignItems: "center",
              gap: 6,
              minWidth: 0,
            }}
            onClick={() => handleDeleteClick(record)}
            loading={deletingId === record.id}
            disabled={deletingId !== null}
            aria-label={t?.delete?.confirm_ok || "Delete"}
          >
            <img
              src="/store/trash.svg"
              alt={t?.delete?.confirm_ok || "Delete"}
              style={{ width: 16, height: 16, display: "block" }}
            />
          </Button>
          ),
      },
    ];
    return base;
  }, [deleteBlocked, t, deletingId, handleDeleteClick]);

  const hasExceeded =
    charCounts.english > MAX_CHARS || charCounts.arabic > MAX_CHARS;

  const renderPaginationItem = (page, type, originalElement) => {
    const totalPages = Math.ceil(pagination.total / pagination.pageSize || 1);

    if (type === "prev") {
      const disabled = isRTL
        ? pagination.current >= totalPages
        : pagination.current === 1;
      return (
        <a
          onClick={(e) => {
            e.preventDefault();
            if (disabled) return;
            const target = isRTL
              ? Math.min(totalPages, pagination.current + 1)
              : Math.max(1, pagination.current - 1);
            setPagination((prev) => ({ ...prev, current: target }));
          }}
          style={{
            color: disabled ? "#D0D5DD" : "#18181B",
            fontSize: "12px",
            fontWeight: 400,
            textDecoration: "none",
            border: "none",
            background: "transparent",
            padding: 0,
            cursor: disabled ? "not-allowed" : "pointer",
            display: "flex",
            alignItems: "center",
            gap: "6px",
            pointerEvents: disabled ? "none" : "auto",
          }}
        >
          {isRTL ? (
            <>
              {t?.pagination?.previous || "Previous"}
              <img
                src="/Campaign/ChevronRight.svg"
                alt="Previous"
                style={{
                  width: "16px",
                  height: "16px",
                  transform: "scaleX(-1)",
                }}
              />
            </>
          ) : (
            <>
              <img
                src="/Campaign/ChevronLeft.svg"
                alt="Previous"
                style={{ width: "16px", height: "16px" }}
              />
              {t?.pagination?.previous || "Previous"}
            </>
          )}
        </a>
      );
    }

    if (type === "next") {
      const disabled = isRTL
        ? pagination.current === 1
        : pagination.current >= totalPages;
      return (
        <a
          onClick={(e) => {
            e.preventDefault();
            if (disabled) return;
            const target = isRTL
              ? Math.max(1, pagination.current - 1)
              : Math.min(totalPages, pagination.current + 1);
            setPagination((prev) => ({ ...prev, current: target }));
          }}
          style={{
            color: disabled ? "#D0D5DD" : "#18181B",
            fontSize: "12px",
            fontWeight: 400,
            textDecoration: "none",
            border: "none",
            background: "transparent",
            padding: 0,
            cursor: disabled ? "not-allowed" : "pointer",
            display: "flex",
            alignItems: "center",
            gap: "6px",
            pointerEvents: disabled ? "none" : "auto",
          }}
        >
          {isRTL ? (
            <>
              <img
                src="/Campaign/ChevronLeft.svg"
                alt="Next"
                style={{
                  width: "16px",
                  height: "16px",
                  transform: "scaleX(-1)",
                }}
              />
              {t?.pagination?.next || "Next"}
            </>
          ) : (
            <>
              {t?.pagination?.next || "Next"}
              <img
                src="/Campaign/ChevronRight.svg"
                alt="Next"
                style={{ width: "16px", height: "16px" }}
              />
            </>
          )}
        </a>
      );
    }

    if (type === "page") {
      const isActive = page === pagination.current;
      return (
        <a
          onClick={(e) => {
            e.preventDefault();
            setPagination((prev) => ({ ...prev, current: page }));
          }}
          style={{
            color: "#18181B",
            backgroundColor: isActive ? "#F9FAFB" : "transparent",
            padding: "8px 14px",
            borderRadius: "8px",
            fontSize: "14px",
            fontWeight: 400,
            border: isActive ? "1px solid #D0D5DD" : "1px solid transparent",
            minWidth: 36,
            textAlign: "center",
            display: "inline-block",
          }}
        >
          {page}
        </a>
      );
    }

    return originalElement;
  };

  return (
    <div
      className="discounts-page-container"
      style={{ direction: isRTL ? "rtl" : "ltr" }}
    >
      <h1 className="discounts-page-title">
        {t?.title || "Announcements"}
      </h1>

      <div
        className="discount-filters-container"
        style={{ justifyContent: "flex-end", alignItems: "center" }}
      >
        <div className="discount-filters-right">
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={openCreateModal}
            className="discount-create-button"
            loading={submitting}
            disabled={readOnly}
          >
            {t?.create_button || "Create Announcement"}
          </Button>
        </div>
      </div>

      <div className="discounts-table-container discounts-table-wrapper">
        <Table
          rowKey="id"
          columns={columns}
          dataSource={announcements}
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: false,
            position: ["bottomRight"],
            itemRender: renderPaginationItem,
            onChange: (page) =>
              setPagination((prev) => ({ ...prev, current: page })),
          }}
          locale={{
            emptyText: t?.empty_state || "No announcements found",
          }}
        />
      </div>

      {/* Delete Confirmation (store-style) */}
      <DeleteConfirmationModal
        visible={showDeleteModal}
        deleteLoading={!!deletingId}
        onCancel={() => {
          if (deletingId) return;
          setShowDeleteModal(false);
          setAnnouncementToDelete(null);
        }}
        onConfirm={confirmDelete}
        title={t?.delete?.confirm_title || "Delete announcement?"}
        content={
          t?.delete?.confirm_message ||
          "Are you sure you want to delete this announcement?"
        }
      />

      <Modal
        open={showModal}
        title={t?.modal?.title || "Create Announcement"}
        onCancel={() => setShowModal(false)}
        footer={
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 12,
            }}
          >
            <Button
              type="primary"
              className="discount-create-button no-hover-btn"
              onClick={handleSubmit}
              loading={submitting}
              disabled={hasExceeded}
              style={{ height: 40, display: "flex", alignItems: "center" }}
            >
              {t?.modal?.submit || t?.create_button || "Create"}
            </Button>
            <Button
              onClick={() => setShowModal(false)}
              disabled={submitting}
              className="no-hover-btn"
              style={{ height: 40, display: "flex", alignItems: "center" }}
            >
              {t?.modal?.cancel || "Cancel"}
            </Button>
          </div>
        }
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onValuesChange={(_, allValues) => {
            setCharCounts({
              english: countChars(allValues.englishText || ""),
              arabic: countChars(allValues.arabicText || ""),
            });
          }}
        >
          <Form.Item
            name="englishText"
            label={t?.english_label || "English Text"}
            rules={[
              {
                required: true,
                message:
                  t?.validation?.english_required ||
                  "English text is required",
              },
            {
              validator: (_, value) => {
                const chars = countChars(value || "");
                if (chars <= MAX_CHARS) return Promise.resolve();
                return Promise.reject(
                  new Error(
                    t?.validation?.max_chars ||
                      `Maximum ${MAX_CHARS} characters allowed`
                  )
                );
              },
            },
            ]}
            extra={
              <span
                style={{
                  display: "block",
                  textAlign: "right",
                  color: charCounts.english > MAX_CHARS ? "#D92D20" : "#667085",
                  fontSize: 12,
                  marginTop: 4,
                }}
              >
                {charCounts.english}/{MAX_CHARS}
              </span>
            }
          >
            <Input.TextArea
              rows={3}
              maxLength={MAX_CHARS}
              placeholder={
                t?.modal?.english_placeholder ||
                "Enter the announcement in English"
              }
            />
          </Form.Item>

          <Form.Item
            name="arabicText"
            label={t?.arabic_label || "Arabic Text"}
            rules={[
              {
                required: true,
                message:
                  t?.validation?.arabic_required ||
                  "Arabic text is required",
              },
              {
                validator: (_, value) => {
                  const chars = countChars(value || "");
                  if (chars <= MAX_CHARS) return Promise.resolve();
                  return Promise.reject(
                    new Error(
                      t?.validation?.max_chars ||
                        `Maximum ${MAX_CHARS} characters allowed`
                    )
                  );
                },
              },
            ]}
            extra={
              <span
                style={{
                  display: "block",
                  textAlign: "left",
                  color: charCounts.arabic > MAX_CHARS ? "#D92D20" : "#667085",
                  fontSize: 12,
                  marginTop: 4,
                  direction: "rtl",
                }}
              >
                {charCounts.arabic}/{MAX_CHARS}
              </span>
            }
          >
            <Input.TextArea
              rows={3}
              maxLength={MAX_CHARS}
              placeholder={
                t?.modal?.arabic_placeholder ||
                "Enter the announcement in Arabic"
              }
              style={{ direction: "rtl", textAlign: "right" }}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Announcements;

